import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Highlight from '@tiptap/extension-highlight';
import Placeholder from '@tiptap/extension-placeholder';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { useEffect, useState, useRef } from 'react';
import EmojiPicker from 'emoji-picker-react';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  Heading1,
  Heading2,
  List,
  ListOrdered,
  Quote,
  Highlighter,
  Undo,
  Redo,
  Type,
  ChevronDown,
  Smile,
} from 'lucide-react';
import VoiceRecorder from './VoiceRecorder';
import { useFeatures, FeatureGate } from '@/context/FeaturesContext';
import { UpgradePrompt, useUpgradePrompt } from '@/components/UpgradePrompt';

const MenuButton = ({ onClick, isActive, disabled, children, title }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={`p-2 rounded-lg transition-all duration-200 ${
      isActive
        ? 'text-white shadow-lg'
        : 'text-[var(--theme-text)]/70 hover:text-[var(--theme-text)] hover:bg-[var(--theme-background-secondary)]'
    } ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
    style={isActive ? { background: 'var(--theme-button-bg)' } : undefined}
  >
    {children}
  </button>
);

const Divider = () => (
  <div className="w-px h-6 bg-[var(--theme-border)]/30 mx-1 hidden sm:block" />
);

// Emoji Picker Dropdown component
const EmojiPickerDropdown = ({ editor }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);
  
  const handleEmojiClick = (emojiData) => {
    if (editor) {
      editor.chain().focus().insertContent(emojiData.emoji).run();
    }
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        title="Insert Emoji"
        className={`p-2 rounded-lg transition-all duration-200 ${
          isOpen
            ? 'text-white shadow-lg'
            : 'text-[var(--theme-text)]/70 hover:text-[var(--theme-text)] hover:bg-[var(--theme-background-secondary)]'
        }`}
        style={isOpen ? { background: 'var(--theme-button-bg)' } : undefined}
        data-testid="emoji-picker-btn"
      >
        <Smile className="w-4 h-4" />
      </button>
      
      {isOpen && (
        <div 
          className="absolute top-full left-0 mt-2 z-[9999]"
          style={{ maxWidth: '350px' }}
        >
          <EmojiPicker 
            onEmojiClick={handleEmojiClick}
            theme="dark"
            emojiStyle="native"
            searchPlaceholder="Search emoji..."
            width={350}
            height={400}
            previewConfig={{ showPreview: false }}
            skinTonesDisabled
            lazyLoadEmojis
          />
        </div>
      )}
    </div>
  );
};

// Color picker dropdown component
const ColorPicker = ({ editor, colors, type }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [customColor, setCustomColor] = useState('#df1283');
  const dropdownRef = useRef(null);
  const colorInputRef = useRef(null);
  
  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);
  
  const getCurrentColor = () => {
    if (type === 'text') {
      // Check preset colors
      for (const c of colors) {
        if (editor.isActive('textStyle', { color: c.color })) {
          return c.color;
        }
      }
      // Check if any text color is active
      const textStyle = editor.getAttributes('textStyle');
      if (textStyle.color) {
        return textStyle.color;
      }
    } else {
      for (const c of colors) {
        if (editor.isActive('highlight', { color: c.color })) {
          return c.color;
        }
      }
      const highlight = editor.getAttributes('highlight');
      if (highlight.color) {
        return highlight.color;
      }
    }
    return null;
  };

  const applyColor = (color) => {
    if (type === 'text') {
      editor.chain().focus().setColor(color).run();
    } else {
      editor.chain().focus().toggleHighlight({ color }).run();
    }
    setIsOpen(false);
  };

  const handleCustomColorChange = (e) => {
    const color = e.target.value;
    setCustomColor(color);
    applyColor(color);
  };

  const currentColor = getCurrentColor();
  const isAnyColorActive = currentColor !== null;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        title={type === 'text' ? 'Text Color' : 'Highlight Color'}
        className={`p-2 rounded-lg transition-all duration-200 flex items-center gap-1 ${
          isAnyColorActive
            ? 'text-white shadow-lg'
            : 'text-[var(--theme-text)]/70 hover:text-[var(--theme-text)] hover:bg-[var(--theme-background-secondary)]'
        }`}
        style={isAnyColorActive ? { background: 'var(--theme-button-bg)' } : undefined}
      >
        {type === 'text' ? (
          <Type className="w-4 h-4" />
        ) : (
          <Highlighter className="w-4 h-4" />
        )}
        <div 
          className="w-3 h-3 rounded-full border border-white/30"
          style={{ backgroundColor: currentColor || 'var(--theme-text)' }}
        />
        <ChevronDown className="w-3 h-3" />
      </button>
      
      {isOpen && (
        <div className="absolute top-full left-0 mt-2 p-3 rounded-xl shadow-2xl z-[9999] min-w-[180px]"
          style={{ 
            backgroundColor: 'var(--theme-background)', 
            border: '1px solid rgba(var(--theme-border-rgb, var(--theme-primary-rgb)), 0.4)',
            boxShadow: '0 10px 40px rgba(0,0,0,0.5)'
          }}
        >
          <p className="text-xs text-[#FDF2F8]/80 mb-3 font-semibold">
            {type === 'text' ? 'Text Color' : 'Highlight'}
          </p>
          
          {/* Preset Colors */}
          <div className="flex flex-wrap gap-2 mb-3">
            {colors.map((c) => (
              <button
                key={c.name}
                type="button"
                onClick={() => applyColor(c.color)}
                title={c.name}
                className={`w-8 h-8 rounded-full border-2 transition-all duration-200 hover:scale-110 ${
                  currentColor === c.color
                    ? 'border-white ring-2 ring-white/50 scale-110'
                    : 'border-white/30 hover:border-white/70'
                }`}
                style={{ backgroundColor: c.color }}
              />
            ))}
          </div>
          
          {/* Custom Color Picker */}
          <div className="pt-3 mb-3" style={{ borderTop: '1px solid rgba(var(--theme-border-rgb, var(--theme-primary-rgb)), 0.3)' }}>
            <p className="text-xs mb-2" style={{ color: 'var(--theme-text-secondary)' }}>Custom Color</p>
            <div className="flex items-center gap-2">
              <div 
                className="relative w-10 h-10 rounded-lg overflow-hidden border-2 border-white/30 cursor-pointer hover:border-white/70 transition-colors"
                onClick={() => colorInputRef.current?.click()}
              >
                <input
                  ref={colorInputRef}
                  type="color"
                  value={customColor}
                  onChange={handleCustomColorChange}
                  className="absolute inset-0 w-full h-full cursor-pointer opacity-0"
                />
                <div 
                  className="w-full h-full"
                  style={{ backgroundColor: customColor }}
                />
              </div>
              <input
                type="text"
                value={customColor}
                onChange={(e) => {
                  const val = e.target.value;
                  if (/^#[0-9A-Fa-f]{0,6}$/.test(val)) {
                    setCustomColor(val);
                    if (val.length === 7) {
                      applyColor(val);
                    }
                  }
                }}
                placeholder="#000000"
                className="flex-1 rounded-lg px-2 py-1 text-sm font-mono w-20"
                style={{ backgroundColor: 'var(--theme-background)', border: '1px solid rgba(var(--theme-border-rgb, var(--theme-primary-rgb)), 0.3)', color: 'var(--theme-text)' }}
              />
            </div>
          </div>
          
          {/* Remove Color */}
          <button
            type="button"
            onClick={() => {
              if (type === 'text') {
                editor.chain().focus().unsetColor().run();
              } else {
                editor.chain().focus().unsetHighlight().run();
              }
              setIsOpen(false);
            }}
            className="w-full text-xs py-1 px-2 rounded transition-colors"
            style={{ color: 'var(--theme-text-secondary)', hover: 'var(--theme-background-secondary)' }}
            onMouseOver={(e) => e.target.style.backgroundColor = 'var(--theme-background-secondary)'}
            onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            Remove {type === 'text' ? 'Color' : 'Highlight'}
          </button>
        </div>
      )}
    </div>
  );
};

const RichTextEditor = ({ content, onChange, placeholder }) => {
  const { isOpen: upgradeOpen, feature: upgradeFeature, limit: upgradeLimit, showUpgradePrompt, hideUpgradePrompt } = useUpgradePrompt();
  
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2],
        },
      }),
      Underline,
      TextStyle,
      Color,
      Highlight.configure({
        multicolor: true,
        HTMLAttributes: {
          class: 'highlight-mark',
        },
      }),
      Placeholder.configure({
        placeholder: placeholder || "What's on your mind? ✨",
        emptyEditorClass: 'is-editor-empty',
      }),
    ],
    content: content || '',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose prose-invert prose-pink max-w-none focus:outline-none min-h-[200px] p-4',
        'data-testid': 'rich-text-content',
      },
    },
  });

  // Handle voice transcription - insert text at cursor
  const handleVoiceTranscription = (text) => {
    if (editor && text) {
      // Insert transcribed text at current cursor position
      editor.chain().focus().insertContent(text + ' ').run();
    }
  };

  // Update content when prop changes (e.g., loading existing entry)
  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content || '');
    }
  }, [content, editor]);

  if (!editor) {
    return (
      <div className="diary-editor-container animate-pulse">
        <div className="h-10 rounded-t-xl" style={{ backgroundColor: 'var(--theme-background-secondary)', opacity: 0.5 }} />
        <div className="h-[250px] rounded-b-xl" style={{ backgroundColor: 'var(--theme-background-secondary)', opacity: 0.3 }} />
      </div>
    );
  }

  const themeColors = [
    { name: 'Primary', color: 'var(--theme-primary)' },
    { name: 'Secondary', color: 'var(--theme-secondary)' },
    { name: 'White', color: '#FFFFFF' },
    { name: 'Light', color: 'var(--theme-text)' },
  ];

  return (
    <div className="diary-editor-container" data-testid="rich-text-editor">
      {/* Formatting Toolbar */}
      <div className="editor-toolbar flex flex-wrap items-center gap-1 p-2 rounded-t-xl" style={{ backgroundColor: 'var(--theme-background-secondary)', borderBottom: '1px solid rgba(var(--theme-border-rgb, var(--theme-primary-rgb)), 0.2)' }}>
        {/* Text Formatting */}
        <MenuButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive('bold')}
          title="Bold (Ctrl+B)"
        >
          <Bold className="w-4 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive('italic')}
          title="Italic (Ctrl+I)"
        >
          <Italic className="w-4 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          isActive={editor.isActive('underline')}
          title="Underline (Ctrl+U)"
        >
          <UnderlineIcon className="w-4 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleStrike().run()}
          isActive={editor.isActive('strike')}
          title="Strikethrough"
        >
          <Strikethrough className="w-4 h-4" />
        </MenuButton>

        <Divider />

        {/* Text Color Picker */}
        <ColorPicker editor={editor} colors={themeColors} type="text" />
        
        {/* Highlight Color Picker */}
        <ColorPicker editor={editor} colors={themeColors.slice(0, 3)} type="highlight" />
        
        {/* Emoji Picker */}
        <EmojiPickerDropdown editor={editor} />

        <Divider />

        {/* Headings */}
        <MenuButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          isActive={editor.isActive('heading', { level: 1 })}
          title="Heading 1"
        >
          <Heading1 className="w-4 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          isActive={editor.isActive('heading', { level: 2 })}
          title="Heading 2"
        >
          <Heading2 className="w-4 h-4" />
        </MenuButton>

        <Divider />

        {/* Lists */}
        <MenuButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          isActive={editor.isActive('bulletList')}
          title="Bullet List"
        >
          <List className="w-4 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          isActive={editor.isActive('orderedList')}
          title="Numbered List"
        >
          <ListOrdered className="w-4 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          isActive={editor.isActive('blockquote')}
          title="Quote"
        >
          <Quote className="w-4 h-4" />
        </MenuButton>

        <Divider />

        {/* Undo/Redo */}
        <MenuButton
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          title="Undo (Ctrl+Z)"
        >
          <Undo className="w-4 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          title="Redo (Ctrl+Shift+Z)"
        >
          <Redo className="w-4 h-4" />
        </MenuButton>

        <Divider />

        {/* Voice Recorder - Feature gated */}
        <FeatureGate feature="voice_typing">
          <VoiceRecorder 
            onTranscription={handleVoiceTranscription} 
            onLimitReached={(feature) => showUpgradePrompt(feature, 10)}
          />
        </FeatureGate>
      </div>

      {/* Editor Content */}
      <div className="editor-content-wrapper rounded-b-xl border border-t-0" style={{ backgroundColor: 'var(--theme-background-secondary)', borderColor: 'rgba(var(--theme-border-rgb, var(--theme-primary-rgb)), 0.2)' }}>
        <EditorContent editor={editor} />
      </div>
      
      {/* Upgrade Prompt */}
      <UpgradePrompt 
        feature={upgradeFeature} 
        limit={upgradeLimit}
        isOpen={upgradeOpen} 
        onClose={hideUpgradePrompt} 
      />
    </div>
  );
};

export default RichTextEditor;
